/**
 * The dashboard layout: the F032 inset sidebar and top bars around every page,
 * plus the app-wide movement search and shortcut help.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import MovementSearch from "../components/movement/MovementSearch";
import {
  OPEN_MOVEMENT_SEARCH_EVENT,
  type OpenMovementSearchDetail,
} from "../components/movement/movementSearchEvents";
import { XPAnnouncerProvider } from "../components/movement/XPAnnouncer";
import KeyboardShortcutsModal from "../components/Booking/KeyboardShortcutsModal";
import SkipToMainContent, { MAIN_CONTENT_ID } from "../components/SkipToMainContent";
import VersionBanner from "../components/VersionBanner";
import { useTrainingRegistry } from "../hooks/useTrainingRegistry";
import { PageShortcutsProvider, usePageShortcuts } from "../contexts/PageShortcutsContext";
import { PageTitleProvider } from "../contexts/PageTitleContext";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import type { KeyboardShortcut } from "../hooks/useKeyboardShortcuts";
import { useRouteFocus } from "../hooks/useRouteFocus";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { SidebarInset, SidebarProvider, cn } from "@lemonadetraining/web-ui";

// Local imports
import { ShellDesktopHeader, ShellMobileHeader, ShellSidebar } from "./ShellSidebar";
import AccessNoticesHost from "../components/access/AccessNoticesHost";

/* ============ Global Movement Search ============ */
// One way to a movement (F032 step 9): ⌘K, the top bar search, ⌘L and every
// "Log a result" open this search, then the movement page; ⌘L and "Log a
// result" open that page's Log result dialog as it lands.
function GlobalMovementSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [logIntent, setLogIntent] = useState(false);
  // The gym's library over the global one, so a gym's own movements are found
  // too (as in the app); read only once the search is first opened.
  const registry = useTrainingRegistry({ enabled: everOpened });

  const openSearch = useCallback((log: boolean) => {
    setEverOpened(true);
    setLogIntent(log);
    setOpen(true);
  }, []);

  const toggle = useCallback(
    (log: boolean) => {
      if (open && logIntent === log) {
        setOpen(false);
        return;
      }
      openSearch(log);
    },
    [open, logIntent, openSearch]
  );

  useKeyboardShortcuts({
    shortcuts: [
      {
        key: "k",
        ctrlKey: true,
        description: "Search movements",
        handler: () => toggle(false),
      },
      {
        key: "l",
        ctrlKey: true,
        description: "Log a result",
        handler: () => toggle(true),
      },
    ],
  });

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenMovementSearchDetail | undefined>).detail;
      openSearch(detail?.log === true);
    };
    window.addEventListener(OPEN_MOVEMENT_SEARCH_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_MOVEMENT_SEARCH_EVENT, onOpen);
  }, [openSearch]);

  const handleSelect = useCallback(
    (movementId: string) => {
      navigate(`/dashboard/movements/${movementId}${logIntent ? "?log=1" : ""}`);
    },
    [navigate, logIntent]
  );

  return (
    <MovementSearch
      open={open}
      onOpenChange={setOpen}
      movements={registry.movements}
      onSelect={handleSelect}
    />
  );
}

/* ============ Global Keyboard Shortcuts Help (?) Wrapper ============ */

// Display-only — handlers are wired by GlobalMovementSearch above; the modal
// just needs the chord chips and labels.
const GLOBAL_SHORTCUT_HELP: ReadonlyArray<KeyboardShortcut> = [
  { key: "k", ctrlKey: true, description: "Search movements", handler: () => {} },
  { key: "l", ctrlKey: true, description: "Log a result", handler: () => {} },
  { key: "?", shiftKey: true, description: "Show keyboard shortcuts", handler: () => {} },
  { key: "Escape", description: "Close dialog", handler: () => {} },
];

function GlobalShortcutHelp() {
  const [open, setOpen] = useState(false);

  // The one help surface app-wide: pages with shortcuts of their own publish
  // them here (PageShortcutsContext) instead of owning a second modal, so the
  // ? key and the header help button behave the same on every route.
  const { pageShortcuts } = usePageShortcuts();
  const modalShortcuts = useMemo(() => {
    if (!pageShortcuts) return [...GLOBAL_SHORTCUT_HELP];
    // Page entries win on key collisions (e.g. its Escape over the global one).
    const pageKeys = new Set(pageShortcuts.map((s) => s.key.toLowerCase()));
    return [
      ...pageShortcuts,
      ...GLOBAL_SHORTCUT_HELP.filter((s) => !pageKeys.has(s.key.toLowerCase())),
    ];
  }, [pageShortcuts]);

  useKeyboardShortcuts({
    shortcuts: [
      {
        key: "?",
        shiftKey: true,
        description: "Show keyboard shortcuts",
        handler: () => setOpen((prev) => !prev),
      },
    ],
  });

  // The header ? button (and any other caller) opens this modal via a global
  // event — same pattern as movement search.
  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener("lt:open-shortcuts-help", handleOpen);
    return () => window.removeEventListener("lt:open-shortcuts-help", handleOpen);
  }, []);

  return (
    <KeyboardShortcutsModal open={open} onClose={() => setOpen(false)} shortcuts={modalShortcuts} />
  );
}

/* ============ Route Focus Manager ============ */
function RouteFocusManager() {
  useRouteFocus();
  return null;
}

/* ============ Main Layout Component ============ */
export default function SidebarLayout() {
  return (
    <XPAnnouncerProvider>
      {/* PageShortcutsProvider spans both the routed pages (<Outlet/>) that
          publish shortcuts and the GlobalShortcutHelp modal that displays them. */}
      <PageShortcutsProvider>
        <PageTitleProvider>
          <SkipToMainContent />
          <SidebarProvider defaultOpen>
            <ShellSidebar />
            <SidebarInset className="flex flex-col">
              <ShellMobileHeader />
              <ShellDesktopHeader />
              <main
                id={MAIN_CONTENT_ID}
                tabIndex={-1}
                className={cn(
                  "flex-1 overflow-auto",
                  // pt- must not sit beside py-: twMerge drops pt-* when a later py-* is present.
                  // 68px = 52px fixed mobile header + 16px content padding.
                  "px-4 pb-4 pt-[68px] md:px-6 md:pb-6 md:pt-6",
                  // Hide the focus ring when programmatic focus moves here
                  // (skip link / route change) — it's a landmark, not interactive.
                  "focus:outline-none"
                )}
              >
                <VersionBanner />
                <AccessNoticesHost>
                  <Outlet />
                </AccessNoticesHost>
              </main>
            </SidebarInset>
            <GlobalMovementSearch />
            <GlobalShortcutHelp />
            <RouteFocusManager />
          </SidebarProvider>
        </PageTitleProvider>
      </PageShortcutsProvider>
    </XPAnnouncerProvider>
  );
}
